import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  UserIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import { EmployeesClient, EmployeeDto } from '../../web-api-client';
import { getOffboardingReasonText } from '../../utils/enumHelpers';
import { formatDateLong as formatDate } from '../../utils/dateHelpers';
import { API_CONFIG } from '../../config/api';

export default function EmployeeDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<EmployeeDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        setLoading(true);
        const client = new EmployeesClient(API_CONFIG.baseUrl);
        const employees = await client.getEmployees(null, null, null);
        const found = employees.find(e => e.id === parseInt(id || '0'));
        if (found) {
          setEmployee(found);
        } else {
          setError('Employee not found');
        }
      } catch (err) {
        console.error('Failed to fetch employee:', err);
        setError('Failed to load employee details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEmployee();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-48"></div>
        <div className="bg-white rounded-2xl p-8">
          <div className="h-6 bg-gray-200 rounded w-64 mb-4"></div>
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/employees')}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Employees
        </button>
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">{error || 'Employee not found'}</h3>
          <p className="mt-1 text-sm text-gray-500">The employee you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate('/employees')}
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-2" />
        Back to Employees
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {employee.fullName}
            </h1>
            <p className="mt-1 text-gray-500">{employee.designation || 'No designation'}</p>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            employee.isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}>
            {employee.isActive ? 'Active' : 'Exited'}
          </span>
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
          <dl className="space-y-4">
            <div className="flex items-start">
              <dt className="flex items-center text-sm text-gray-500 w-40">
                <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                Employee No.
              </dt>
              <dd className="text-sm text-gray-900">{employee.employeeNumber || '-'}</dd>
            </div>
            <div className="flex items-start">
              <dt className="flex items-center text-sm text-gray-500 w-40">
                <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                Work Email
              </dt>
              <dd className="text-sm text-gray-900">{employee.workEmail || '-'}</dd>
            </div>
            <div className="flex items-start">
              <dt className="flex items-center text-sm text-gray-500 w-40">
                <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                Personal Email
              </dt>
              <dd className="text-sm text-gray-900">{employee.personalEmail || '-'}</dd>
            </div>
          </dl>
        </div>

        {/* Work Information */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Work Information</h2>
          <dl className="space-y-4">
            <div className="flex items-start">
              <dt className="flex items-center text-sm text-gray-500 w-40">
                <BuildingOfficeIcon className="h-4 w-4 mr-2 text-gray-400" />
                Department
              </dt>
              <dd className="text-sm text-gray-900">{employee.department || '-'}</dd>
            </div>
            <div className="flex items-start">
              <dt className="flex items-center text-sm text-gray-500 w-40">
                <BriefcaseIcon className="h-4 w-4 mr-2 text-gray-400" />
                Designation
              </dt>
              <dd className="text-sm text-gray-900">{employee.designation || '-'}</dd>
            </div>
            <div className="flex items-start">
              <dt className="flex items-center text-sm text-gray-500 w-40">
                <MapPinIcon className="h-4 w-4 mr-2 text-gray-400" />
                Location
              </dt>
              <dd className="text-sm text-gray-900">{employee.location || '-'}</dd>
            </div>
          </dl>
        </div>

        {/* Reporting Structure */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Reporting Structure</h2>
          <dl className="space-y-4">
            <div className="flex items-start">
              <dt className="flex items-center text-sm text-gray-500 w-40">
                <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                Immediate Superior
              </dt>
              <dd className="text-sm text-gray-900">{employee.immediateSuperior || '-'}</dd>
            </div>
            <div className="flex items-start">
              <dt className="flex items-center text-sm text-gray-500 w-40">
                <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                Second Level Approver Email
              </dt>
              <dd className="text-sm text-gray-900">{employee.reportingSuperior || '-'}</dd>
            </div>
          </dl>
        </div>

        {/* Employment Dates */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Employment Dates</h2>
          <dl className="space-y-4">
            <div className="flex items-start">
              <dt className="flex items-center text-sm text-gray-500 w-40">
                <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                Join Date
              </dt>
              <dd className="text-sm text-gray-900">{formatDate(employee.employmentDate)}</dd>
            </div>
            {!employee.isActive && (
              <>
                <div className="flex items-start">
                  <dt className="flex items-center text-sm text-gray-500 w-40">
                    <BriefcaseIcon className="h-4 w-4 mr-2 text-gray-400" />
                    Type of Exit
                  </dt>
                  <dd className="text-sm text-gray-900">{getOffboardingReasonText(employee.offboardingReason)}</dd>
                </div>
                <div className="flex items-start">
                  <dt className="flex items-center text-sm text-gray-500 w-40">
                    <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                    Date of Exit
                  </dt>
                  <dd className="text-sm text-gray-900">{formatDate(employee.lastWorkingDate)}</dd>
                </div>
              </>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}
